import { FC, Fragment, useCallback } from 'react'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { Color } from 'constants/assets'
import { EditSavedPaymentMethodPopup } from '../EditSavedPaymentMethodPopup'
import { MUIButton } from 'components/common/MUIButton'
import { QueryStatus } from 'components/common/QueryStatus'
import ReactLoading from 'react-loading'
import { SavedPaymentMethodListCard } from 'components/common/SavedPaymentMethodCard/ListCard'
import { useActionPopup } from 'utils/hooks'
import { useDeleteStripePaymentMethod } from 'dataQueries/payment.query'
import { useEditPaymentMethodContext } from './contexts'
import { useSavedStripePaymentMethods } from 'components/contexts/SavedStripePaymentMethodsContext'

/**
 * Show list of saved payment methods with edit and delete actions.
 * MUST to be inside *SavedStripePaymentMethods* context provider!
 * @example <SavedPaymentMethodsRedesignController />
 */
export const SavedPaymentMethodsRedesignController: FC = () => {

  const { setEditPaymentMethodModal } = useEditPaymentMethodContext()

  const { t } = useTranslation(['saved_payment_methods_list', 'saved_payment_methods_delete'])
  const { showConfirm } = useActionPopup()
  const {
    savedStripePaymentMethods,
    stripePaymentMethods
  } = useSavedStripePaymentMethods()

  const deleteStripePaymentMethod = useDeleteStripePaymentMethod()

  const handleDelete = useCallback(async (paymentMethodId: string) => {
    if (!(await showConfirm(
      t('saved_payment_methods_delete:confirm_text'),
      {
        title: t('saved_payment_methods_delete:confirm_title')
      }
    ))) return

    deleteStripePaymentMethod.mutate({ paymentMethodId }, {
      onSuccess: () => {
        stripePaymentMethods?.refetch()
      }
    })
  }, [deleteStripePaymentMethod, showConfirm, stripePaymentMethods, t])

  return (
    <Stack gap="1.6rem">
      <Stack gap=".4rem">
        <Typography variant="text-xl" fontWeight={600} color={GRAY_900}>
          {t('title')}
        </Typography>
      </Stack>

      <BorderBoxWrapper padding={2.4} gap={1.6}>

        {/* LOADING */}
        {stripePaymentMethods?.isPending &&
          <ReactLoading
            type="cylon"
            color={Color.GRAY_TEXT}
          />
        }

        {(stripePaymentMethods?.isSuccess || stripePaymentMethods?.isError) &&
          <Fragment>

            {/* EMPTY */}
            {(!savedStripePaymentMethods || savedStripePaymentMethods.length === 0) &&
              <Stack gap={1}>

                <Typography variant="text-md" fontWeight={500} color={GRAY_900}>
                  {t('empty_title')}
                </Typography>

                <Typography variant="text-sm" fontWeight={400} color={GRAY_700}>
                  {/* Translation has strong tags */}
                  <Trans t={t} i18nKey="empty_text">
                    &nbsp;
                  </Trans>
                </Typography>

              </Stack>
            }

            {/* NOT EMPTY */}
            {!!savedStripePaymentMethods && savedStripePaymentMethods.length > 0 &&
              <Fragment>
                <Stack gap={2}>

                  {savedStripePaymentMethods.map((savedMethod) => {
                    return (
                      <SavedPaymentMethodListCard
                        key={savedMethod.stripeId}
                        paymentMethod={savedMethod}
                      >

                        <MUIButton
                          type="secondaryBorder"
                          onClick={() => setEditPaymentMethodModal(savedMethod.stripeId)}
                          isLoading={deleteStripePaymentMethod.isPending}
                          disabled={deleteStripePaymentMethod.isPending}
                        >
                          {t('edit')}
                        </MUIButton>

                        <MUIButton
                          type="orangeSecondaryNoBorder"
                          onClick={() => handleDelete(savedMethod.stripeId)}
                          isLoading={deleteStripePaymentMethod.isPending}
                          disabled={deleteStripePaymentMethod.isPending}
                        >
                          {t('delete')}
                        </MUIButton>

                      </SavedPaymentMethodListCard>
                    )
                  })}

                  <QueryStatus
                    query={deleteStripePaymentMethod}
                    errorMessage={t('saved_payment_methods_delete:error')}
                    showStates={['error', 'pending']}
                    spaceTopRem={2}
                  />

                </Stack>

                <EditSavedPaymentMethodPopup />

              </Fragment>
            }

          </Fragment>
        }

      </BorderBoxWrapper>
    </Stack>
  )
}
